"use client"

import { useCallback } from "react"
import Image from "next/image"
import Link from "next/link"
import { useParams } from "next/navigation"
import { Heart, Minus, Plus, ShoppingCart } from "lucide-react"
import { toast } from "sonner"

import { DishItem } from "@/lib/types"
import {
  formatCurrency,
  formatRatingCount,
  getPlaceHolderDishImage,
} from "@/lib/utils"

interface MenuItemProps {
  item: DishItem
  onSelect?: () => void
  currentItemQuantity?: number
  isUpdating?: boolean
  onUpdateQuantity?: (
    itemId: number,
    newQuantity: number,
    itemName: string,
    price: number,
    currentQuantity: number,
    hasCustomizations: boolean
  ) => void
  isFlashSaleCategory?: boolean
  categoryActive?: boolean
  itemIsActive?: boolean
  maxAllowPerDay?: number
  usedQuantityInDay?: number
  isRestoClosed?: boolean
}

export function MenuItem({
  item,
  onSelect,
  currentItemQuantity = 0,
  isUpdating = false,
  onUpdateQuantity,
  isFlashSaleCategory = false,
  categoryActive = true,
  itemIsActive = true,
  maxAllowPerDay = -1,
  usedQuantityInDay = 0,
  isRestoClosed = false,
}: MenuItemProps) {
  const quantity = currentItemQuantity
  const params = useParams<{ id: string }>()
  const restaurantId = params.id

  // Check if item has customizations
  const hasCustomizations =
    item.customize_item && item.customize_item.length > 0

  // Flash Sale Logic
  const isFlashSaleItem =
    maxAllowPerDay !== -1 && usedQuantityInDay < maxAllowPerDay

  // Overall disabled state
  const isDisabled =
    !itemIsActive ||
    !categoryActive ||
    (isFlashSaleCategory && usedQuantityInDay >= maxAllowPerDay)
  const offersLeft = maxAllowPerDay - usedQuantityInDay

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent dialog opening

    if (isDisabled) return // Prevent action if disabled

    if (quantity > 0) {
      // Ensure we don't go below 0
      const newQuantity = quantity - 1
      if (onUpdateQuantity) {
        onUpdateQuantity(
          item.restaurant_item_id,
          newQuantity,
          item.item_name,
          item.price,
          quantity, // Quantity before this decrement
          hasCustomizations
        )
      }
      return newQuantity
    }
    return quantity // No change if already 0
  }

  const handleIncrement = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent dialog opening

      if (isDisabled) return // Prevent action if disabled
      // Additional check for flash sale quantity limit before incrementing
      if (isFlashSaleCategory && quantity >= maxAllowPerDay) {
        // Optionally, show a specific toast or log, but primarily prevent update
        toast.error("Bạn đã đạt giới hạn mua sản phẩm này.")
        return
      }

      // Double check limit with prevQuantity in case of rapid clicks / state lag
      if (isFlashSaleCategory && quantity >= maxAllowPerDay) {
        return quantity // Don't update if limit is already met
      }
      const newQuantity = quantity + 1
      if (onUpdateQuantity) {
        onUpdateQuantity(
          item.restaurant_item_id,
          newQuantity,
          item.item_name,
          item.price,
          quantity, // Quantity before this increment
          hasCustomizations
        )
      }
      return newQuantity
    },
    [
      isDisabled,
      isFlashSaleCategory,
      quantity,
      maxAllowPerDay,
      onUpdateQuantity,
      item.restaurant_item_id,
      item.item_name,
      item.price,
      hasCustomizations,
    ]
  )
  // Main click handler, consider disabled state
  const handleItemClick = () => {
    if (isDisabled) return
    onSelect?.()
  }

  return (
    <article
      className={`flex flex-row border rounded-2xl overflow-hidden w-full max-w-full group ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      itemScope
      itemType="https://schema.org/MenuItem"
      onClick={handleItemClick}
      onKeyDown={(e) => {
        if (isDisabled) return
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleItemClick()
        }
      }}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="button"
      tabIndex={isDisabled ? -1 : 0}
    >
      {/* Hidden SEO-friendly link to dish detail page */}
      <div className="sr-only">
        <Link
          href={`/resto/${restaurantId}/dish/${item.restaurant_item_id}`}
          itemProp="url"
        >
          Xem chi tiết {item.item_name}
        </Link>
      </div>

      {/* Image at the top */}
      <div
        className={`relative shrink-0 aspect-square size-36 overflow-hidden`}
      >
        <Image
          src={item.item_image_compressed_web || "/dish_thumbnail.webp"}
          alt={`Hình ảnh ${item.item_name}`}
          fill
          placeholder="blur"
          blurDataURL={getPlaceHolderDishImage()}
          sizes="(max-width: 768px) 100vw, 300px"
          className={`object-cover transition-transform duration-300 ease-out ${!isDisabled ? "group-hover:scale-105" : ""}`}
          itemProp="image"
          quality={90}
        />
      </div>

      {/* Content below the image */}
      <div className="flex flex-col p-3 flex-grow relative">
        <h3
          className="font-semibold text-[15px] text-gray-900 line-clamp-2 font-be-vietnam"
          itemProp="name"
        >
          {item.item_name}
        </h3>

        {item.item_details && (
          <p
            className="text-sm text-gray-500 mt-1 line-clamp-1 font-be-vietnam"
            itemProp="description"
          >
            {item.item_details}
          </p>
        )}

        {/* Stats (likes and orders) */}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 font-be-vietnam">
          <div className="flex items-center gap-1">
            <Heart size={12} aria-hidden="true" />
            <span>{item.like_count > 999 ? "999+" : item.like_count}</span>
          </div>
          <div className="flex items-center gap-1">
            <ShoppingCart size={12} aria-hidden="true" />
            <span>Đã bán {formatRatingCount(item.order_count)}</span>
          </div>
        </div>

        {/* Flash Sale Limit Info - Placed before price/actions */}

        {isFlashSaleItem ? (
          <span className="text-orange-600 text-xs mt-1">
            Ưu đãi trong ngày: Còn {offersLeft > 0 ? offersLeft : 0} lượt
          </span>
        ) : null}

        {/* Price and quantity controls */}
        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex items-start flex-wrap gap-x-2">
            <span
              className="text-sm font-semibold text-gray-900 font-be-vietnam"
              itemProp="offers"
              itemScope
              itemType="https://schema.org/Offer"
            >
              <meta itemProp="price" content={item.price.toString()} />
              <meta itemProp="priceCurrency" content="VND" />
              {formatCurrency(item.price)}
            </span>
            {item.offers_discount && item.display_old_price && (
              <span className="text-xs text-gray-500 line-through font-be-vietnam">
                {formatCurrency(item.old_price)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 absolute right-3 bottom-3">
            {isDisabled ? null : quantity === 0 ? (
              <button
                onClick={handleIncrement}
                className="size-8 rounded-full border-gray-200 flex items-center justify-center bg-be-yellow-500 border-0 transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Thêm vào giỏ hàng"
                disabled={
                  isUpdating ||
                  isDisabled ||
                  isRestoClosed ||
                  (isFlashSaleCategory && quantity >= maxAllowPerDay)
                }
              >
                {isUpdating ? (
                  <div className="size-3 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Plus
                    size={16}
                    className="text-gray-900"
                    aria-hidden="true"
                    strokeWidth={2}
                  />
                )}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDecrement}
                  className="size-8 rounded-full flex items-center justify-center bg-gray-200/50 border-0 transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Giảm số lượng"
                  disabled={isRestoClosed || isUpdating || isDisabled}
                >
                  {isUpdating && quantity === 1 ? (
                    <div className="size-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Minus size={16} aria-hidden="true" strokeWidth={1} />
                  )}
                </button>
                <span className="w-4 text-center text-sm font-medium">
                  {isUpdating ? "..." : quantity}
                </span>
                <button
                  onClick={handleIncrement}
                  className="size-8 rounded-full flex items-center justify-center bg-be-yellow-500 border-0 transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Tăng số lượng"
                  disabled={
                    isRestoClosed ||
                    isUpdating ||
                    isDisabled || // General disable check
                    (isFlashSaleCategory && quantity >= maxAllowPerDay) // Flash sale specific limit on current quantity
                  }
                >
                  {isUpdating && quantity > 0 ? (
                    <div className="size-3 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Plus
                      size={16}
                      className="text-gray-900"
                      aria-hidden="true"
                      strokeWidth={2}
                    />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
